import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatINR } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Alert-Me button + modal on each instrument card.  Guest users see a login
 * prompt; authenticated partners can drop a target that fires an email the
 * moment the Vinci desk crosses it (in either direction).
 */
export const AlertButton = ({ category, label, unit, price, color, onCreated, compact = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState(null); // "below" | "above" | null

  const applyPreset = (kind) => {
    const pct = kind === "below" ? 0.97 : 1.03;
    const v = Math.round(price * pct);
    setTarget(String(v));
    setPreset(kind);
  };

  const submit = async () => {
    const t = parseFloat(target);
    if (!t || t <= 0) {
      toast.error("Enter a target price");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/alerts`, { category, target_price: t }, { withCredentials: true });
      toast.success(`Alert set at ${formatINR(t)}. We'll email you the moment the desk crosses it.`);
      setOpen(false);
      setTarget("");
      setPreset(null);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create alert");
    } finally {
      setBusy(false);
    }
  };

  const openModal = () => {
    if (!user) {
      toast.info("Sign in to set price alerts");
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        data-testid={`alert-button-${category}`}
        onClick={openModal}
        title={compact ? `Set a target price for ${label}` : undefined}
        className={compact
          ? "inline-flex items-center justify-center w-6 h-6 rounded-full border transition-colors hover:bg-white/[0.06]"
          : "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border font-grotesk text-[9px] uppercase tracking-[0.16em] transition-colors hover:bg-white/[0.04]"}
        style={{ color, borderColor: `${color}55` }}
      >
        <Bell size={11} />{!compact && " Alert me"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 10, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md rounded-sm border border-white/15 bg-[#0a0a0c] p-6 overflow-hidden"
              style={{ boxShadow: `0 20px 60px rgba(0,0,0,0.6), inset 0 0 0 1px ${color}22` }}
              onClick={(e) => e.stopPropagation()}
              data-testid="alert-modal"
            >
              <div className="absolute inset-x-0 top-0 h-[2px]"
                style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-[#8A8A82] hover:text-white"
                aria-label="Close"
                data-testid="alert-modal-close"
              >
                <X size={16} />
              </button>

              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-sm border"
                  style={{ color, borderColor: `${color}66`, background: `${color}12` }}>
                  <Bell size={12} />
                </span>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color }}>
                  Set a target
                </span>
              </div>
              <h3 className="font-serif text-2xl leading-tight text-[#F5F5F0]">{label}</h3>
              <p className="font-body text-xs text-[#8A8A82] mt-1">
                Trading now at <b className="text-[#F5F5F0]">{formatINR(Math.round(price))}</b> {unit}.
                We'll email you the moment the desk crosses your target — up or down.
              </p>

              <div className="flex items-center gap-2 mt-4">
                <button
                  type="button"
                  data-testid="alert-preset-below"
                  onClick={() => applyPreset("below")}
                  className={`px-3 py-1 rounded-full text-[10px] font-grotesk uppercase tracking-[0.16em] border transition-colors ${
                    preset === "below" ? "border-[#2FB999] text-[#2FB999] bg-[#2FB999]/10" : "border-white/15 text-[#C9C9C2] hover:border-white/30"
                  }`}
                >
                  −3% dip
                </button>
                <button
                  type="button"
                  data-testid="alert-preset-above"
                  onClick={() => applyPreset("above")}
                  className={`px-3 py-1 rounded-full text-[10px] font-grotesk uppercase tracking-[0.16em] border transition-colors ${
                    preset === "above" ? "border-[#FF5A5F] text-[#FF5A5F] bg-[#FF5A5F]/10" : "border-white/15 text-[#C9C9C2] hover:border-white/30"
                  }`}
                >
                  +3% spike
                </button>
              </div>

              <div className="mt-4">
                <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1.5">Target price</label>
                <div className="flex items-center gap-2 border border-white/15 rounded-sm px-3 py-2.5 focus-within:border-white/40 transition-colors">
                  <span className="font-serif text-lg text-[#8A8A82]">₹</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    autoFocus
                    value={target}
                    onChange={(e) => { setTarget(e.target.value); setPreset(null); }}
                    className="bg-transparent flex-1 outline-none font-serif text-lg tabular-nums"
                    placeholder="0.00"
                    data-testid="alert-target-input"
                  />
                  <span className="font-body text-[10px] text-[#8A8A82]">{unit}</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-full border border-white/15 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#C9C9C2] hover:border-white/35"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="alert-submit"
                  disabled={busy}
                  onClick={submit}
                  className="px-5 py-2 rounded-full font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-60"
                  style={{ background: color, color: "#0a0a0a" }}
                >
                  {busy ? "Setting…" : "Set alert"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
